import React from 'react'
import { cn } from '@amusnet/react-ui'

import speedButton0 from '@assets/speed-button-0.svg?inline'
import speedButton1 from '@assets/speed-button-1.svg?inline'
import speedButton2 from '@assets/speed-button-2.svg?inline'

import smallSpeedButton0 from '@assets/speed-button-small-enabled-0.svg?inline'
import smallSpeedButton1 from '@assets/speed-button-small-enabled-1.svg?inline'
import smallSpeedButton2 from '@assets/speed-button-small-enabled-2.svg?inline'
import smallSpeedButton3 from '@assets/speed-button-small-enabled-3.svg?inline'

import smallSpeedButtonEQ0 from '@assets/jackpot-eq/speed-button-small-enabled-0.svg?inline'
import smallSpeedButtonEQ1 from '@assets/jackpot-eq/speed-button-small-enabled-1.svg?inline'
import smallSpeedButtonEQ2 from '@assets/jackpot-eq/speed-button-small-enabled-2.svg?inline'
import smallSpeedButtonEQ3 from '@assets/jackpot-eq/speed-button-small-enabled-3.svg?inline'

import newSpeedButton0 from '@assets/speed-buttons/speed-button-0.svg?inline'
import newSpeedButton1 from '@assets/speed-buttons/speed-button-1.svg?inline'
import newSpeedButton2 from '@assets/speed-buttons/speed-button-2.svg?inline'
import newSpeedButton3 from '@assets/speed-buttons/speed-button-3.svg?inline'

import newSmallSpeedButton0 from '@assets/speed-buttons/speed-button-small-0.svg?inline'
import newSmallSpeedButton1 from '@assets/speed-buttons/speed-button-small-1.svg?inline'
import newSmallSpeedButton2 from '@assets/speed-buttons/speed-button-small-2.svg?inline'
import newSmallSpeedButton3 from '@assets/speed-buttons/speed-button-small-3.svg?inline'

import ultraPlayIcon from '@assets/speed-buttons/ultraplay.svg?inline'
import ultraPlayIconSmall from '@assets/speed-buttons/ultraplay-small.svg?inline'

import { withAutoBlur } from '../../../utils/misc'
import { GameUIMessageType, SpeedClickedMessage } from 'game-ui-types'
import { useConfigurationStore, useJackpotStore, useSoundStore, useUltraPlayStore } from '@stores'
import { useHasActiveBonus } from '@hooks'
import { SOUNDS } from '@constants/sounds'

interface SpeedButtonProps {
    isActive: boolean
    speedLevel: number
    isUltraPlayButtonActive?: boolean
}

const GOLD_DIVIDER = 'linear-gradient(to bottom, #FCF1BD, #E5BC53, #F5DF96, #B8A050, #8A6D28)'

const SpeedButton: React.FC<SpeedButtonProps> = ({ isActive, speedLevel, isUltraPlayButtonActive }) => {
    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)
    const isJackpotRoundUnfinished = useJackpotStore((state) => state.isJackpotRoundUnfinished)
    const shouldEnableUltraSpeed = useConfigurationStore((state) => state.shouldEnableUltraSpeed)
    const hasUltraPlay = useConfigurationStore((state) => state.hasUltraPlay)
    const isUltraPlaySupported = useConfigurationStore((state) => state.isUltraPlaySupported)

    const openUltraPlay = useUltraPlayStore((state) => state.open)
    const ultraPlayStatus = useUltraPlayStore((state) => state.status)
    const isUltraPlayPlaying = ultraPlayStatus === 'PLAYING'
    // During an active bonus the bet is locked, so Ultra Play (which can change the bet) is disabled.
    const hasActiveBonus = useHasActiveBonus()
    const playEngineSound = useSoundStore((state) => state.playEngineSound)

    const isUltraPlayBlocked = hasActiveBonus || isJackpotRoundUnfinished

    const handleOpenUltraPlay = () => {
        playEngineSound(SOUNDS.SHOW_DENOMINATIONS)
        openUltraPlay()
    }

    const handleSpeedClicked = withAutoBlur(() => {
        const msg: SpeedClickedMessage = { type: GameUIMessageType.SPEED_CLICKED }

        window.postMessage(msg)
    })

    if (hasUltraPlay && isUltraPlaySupported) {
        const newTextures = !isEQJackpotType
            ? [newSpeedButton0, newSpeedButton1, newSpeedButton2, newSpeedButton3]
            : [newSmallSpeedButton0, newSmallSpeedButton1, newSmallSpeedButton2, newSmallSpeedButton3]

        const ultraPlayTexture = !isEQJackpotType ? ultraPlayIcon : ultraPlayIconSmall

        const idx = Math.min(speedLevel, newTextures.length - 1)

        return (
            <div className="flex h-[58px]">
                <button
                    id="speed-button"
                    onClick={(e) => handleSpeedClicked(e)}
                    className={cn('focus:outline-none', !isActive && 'opacity-50 pointer-events-none')}
                >
                    <img src={newTextures[idx]} alt="Speed" className="h-full" />
                </button>

                <div className="w-[4px] h-full flex-shrink-0" style={{ background: GOLD_DIVIDER }} />

                <button
                    id="ultra-play-button"
                    onClick={isUltraPlayBlocked ? undefined : handleOpenUltraPlay}
                    className={cn(
                        'focus:outline-none relative',
                        (!isUltraPlayButtonActive || isUltraPlayBlocked) && 'opacity-50 pointer-events-none'
                    )}
                    aria-label="Open Ultra Play"
                >
                    <img src={ultraPlayTexture} alt="Ultra Play" className="h-full" />
                    {isUltraPlayPlaying && (
                        <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#4CAF50]" />
                    )}
                </button>
            </div>
        )
    }

    const normalTextures = [speedButton0, speedButton1, speedButton2]
    const ultraSpeedTextures = !isEQJackpotType
        ? [smallSpeedButton0, smallSpeedButton1, smallSpeedButton2, smallSpeedButton3]
        : [smallSpeedButtonEQ0, smallSpeedButtonEQ1, smallSpeedButtonEQ2, smallSpeedButtonEQ3]

    const textures = shouldEnableUltraSpeed ? ultraSpeedTextures : normalTextures
    const index = Math.min(speedLevel, textures.length - 1)

    return (
        <button
            id="speed-button"
            className={cn('w-[110px] h-[58px] focus:outline-none', !isActive && 'opacity-50 pointer-events-none')}
            onClick={(e) => handleSpeedClicked(e)}
        >
            <img src={textures[index]} alt="Speed Button" className="w-full h-full" />
        </button>
    )
}

export default SpeedButton
